import React, { useState, useEffect } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useShopifyCart } from '../../hooks/useShopifyCart';
import { ShopifyProduct } from '../../services/shopifyStorefront';

interface ProductCardProps {
  productId: string;
  onAddToCart?: () => void;
  enableCarousel?: boolean; // New prop to control carousel functionality
  autoPlay?: boolean; // New prop to control auto-play behavior
}

export const ProductCard: React.FC<ProductCardProps> = ({ 
  productId, 
  onAddToCart, 
  enableCarousel = true, // Default to true to maintain existing behavior
  autoPlay = false // Default to false for more controlled behavior
}) => {
  const theme = useTheme();
  const { addToCart, isLoading, error, getProduct } = useShopifyCart();
  const [product, setProduct] = useState<ShopifyProduct | null>(null);
  const [productLoading, setProductLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const productData = await getProduct(productId);
        setProduct(productData);
      } catch (err) {
        console.error('Error fetching product:', err);
      } finally {
        setProductLoading(false);
      }
    };

    fetchProduct();
  }, [productId, getProduct]);

  // Auto-play functionality - only when auto-play is enabled
  useEffect(() => {
    if (!product || product.images.length <= 1 || isHovered || !autoPlay) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, [product, isHovered, autoPlay]);

  const handleAddToCart = async () => {
    if (!product || !product.variants.length) return;

    setAddingToCart(true);
    try {
      // Get the first available variant
      const availableVariant = product.variants.find(v => v.availableForSale);
      if (!availableVariant) {
        throw new Error('Product is out of stock');
      }

      await addToCart(availableVariant.id);
      onAddToCart?.();
    } catch (err) {
      console.error('Error adding to cart:', err);
    } finally {
      setAddingToCart(false);
    }
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product) return;
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? product.images.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product) return;
    setCurrentImageIndex((prevIndex) => 
      prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handleDotClick = (index: number) => {
    setCurrentImageIndex(index);
  };

  const formatPrice = (amount: string, currencyCode: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(parseFloat(amount));
  };

  if (productLoading) {
    return (
      <Card sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Card>
    );
  }

  if (!product) {
    return (
      <Card sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3
      }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load product
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Product ID: {productId}
        </Typography>
      </Card>
    );
  }

  const currentImage = product.images[currentImageIndex];
  const mainVariant = product.variants[0];
  const isAvailable = product.variants.some(v => v.availableForSale);
  const hasMultipleImages = product.images.length > 1;
  const showCarouselUI = enableCarousel && hasMultipleImages; // Only show carousel UI when enabled and multiple images exist

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
        },
      }}
    >
      {/* Product Image Carousel */}
      {currentImage && (
        <Box 
          sx={{ 
            position: 'relative', 
            height: 250,
            overflow: 'hidden',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <CardMedia
            component="img"
            image={currentImage.url}
            alt={currentImage.altText || product.title}
            sx={{
              height: '100%',
              objectFit: 'cover',
              transition: 'opacity 0.3s ease-in-out',
            }}
          />

          {/* Navigation Arrows - Only show if multiple images */}
          {showCarouselUI && (
            <>
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  color: 'black',
                  width: 32,
                  height: 32,
                  opacity: isHovered ? 1 : 0,
                  transition: 'opacity 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
              >
                <ArrowBackIosIcon sx={{ fontSize: 16 }} />
              </IconButton>

              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  color: 'black',
                  width: 32,
                  height: 32,
                  opacity: isHovered ? 1 : 0,
                  transition: 'opacity 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </>
          )}

          {/* Dots Indicator - Only show if multiple images */}
          {showCarouselUI && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 12,
                padding: '4px 8px',
              }}
            >
              {product.images.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => handleDotClick(index)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: index === currentImageIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'white',
                    },
                  }}
                />
              ))}
            </Box>
          )}

          {/* Image Count Badge - Only show if multiple images */}
          {showCarouselUI && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '2px 6px',
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              {currentImageIndex + 1}/{product.images.length}
            </Box>
          )}
        </Box>
      )}

      {/* Product Content */}
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography gutterBottom variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
          {product.title}
        </Typography>

        {product.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              flexGrow: 1,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {product.description}
          </Typography>
        )}

        {/* Price */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" color="primary" fontWeight="bold">
              {formatPrice(mainVariant.price.amount, mainVariant.price.currencyCode)}
            </Typography>
            {mainVariant.compareAtPrice && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textDecoration: 'line-through' }}
              >
                {formatPrice(mainVariant.compareAtPrice.amount, mainVariant.compareAtPrice.currencyCode)}
              </Typography>
            )}
          </Box>
          
          {/* Stock Status */}
          {!isAvailable && (
            <Chip
              label="Out of Stock"
              color="error"
              size="small"
              sx={{ mt: 1 }}
            />
          )}
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Add to Cart Button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleAddToCart}
          disabled={!isAvailable || isLoading || addingToCart}
          sx={{
            backgroundColor: theme.palette.secondary.main,
            color: 'black',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: theme.palette.secondary.dark,
            },
            '&:disabled': {
              backgroundColor: theme.palette.grey[300],
              color: theme.palette.grey[500],
            },
          }}
        >
          {addingToCart ? (
            <CircularProgress size={20} color="inherit" />
          ) : !isAvailable ? (
            'Out of Stock'
          ) : (
            'Add to Cart'
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProductCard; 